import React, { useMemo } from "react";

type LatLng = [number, number];

type MapBounds = {
  ne: LatLng;
  sw: LatLng;
};

type MapState = {
  bounds: MapBounds;
  zoom: number;
  center: LatLng;
  width: number;
  height: number;
};

export type MapTileOverlayProps = {
  /** Injected by pigeon-maps */
  mapState?: MapState;
  latLngToPixel?: (latLng: LatLng, center?: LatLng, zoom?: number) => [number, number];
  urlTemplate: string;
  opacity?: number;
  blendMode?: React.CSSProperties["mixBlendMode"];
  maxNativeZoom?: number;
  minZoom?: number;
  zIndex?: number;
};

const lng2tile = (lon: number, zoom: number) => ((lon + 180) / 360) * Math.pow(2, zoom);
const lat2tile = (lat: number, zoom: number) =>
  ((1 -
    Math.log(
      Math.tan((lat * Math.PI) / 180) + 1 / Math.cos((lat * Math.PI) / 180)
    ) /
      Math.PI) /
    2) *
  Math.pow(2, zoom);

const tile2lng = (x: number, z: number) => (x / Math.pow(2, z)) * 360 - 180;
const tile2lat = (y: number, z: number) => {
  const n = Math.PI - (2 * Math.PI * y) / Math.pow(2, z);
  return (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
};

function wrapTileX(x: number, z: number) {
  const n = Math.pow(2, z);
  return ((x % n) + n) % n;
}

/**
 * Raster XYZ overlay stacked inside a pigeon-maps &lt;Map&gt;.
 * Uses injected mapState / latLngToPixel so tiles stay aligned while panning/zooming.
 */
const MapTileOverlay: React.FC<MapTileOverlayProps> = ({
  mapState,
  latLngToPixel,
  urlTemplate,
  opacity = 0.45,
  blendMode = "normal",
  maxNativeZoom = 18,
  minZoom = 0,
  zIndex = 1,
}) => {
  const tiles = useMemo(() => {
    if (!mapState?.bounds || !latLngToPixel || mapState.zoom < minZoom) {
      return [];
    }

    const z = Math.min(Math.max(0, Math.round(mapState.zoom)), maxNativeZoom);
    const { sw, ne } = mapState.bounds;
    const maxIndex = Math.pow(2, z) - 1;

    let x0 = Math.floor(lng2tile(sw[1], z));
    let x1 = Math.floor(lng2tile(ne[1], z));
    const y0 = Math.max(0, Math.floor(lat2tile(ne[0], z)));
    const y1 = Math.min(maxIndex, Math.floor(lat2tile(sw[0], z)));

    // World wrap: expand a bit if the range crosses the antimeridian awkwardly
    if (x1 < x0) {
      x1 += Math.pow(2, z);
    }

    // Pad one tile so edges don't flash while panning
    x0 -= 1;
    x1 += 1;

    const out: Array<{
      key: string;
      url: string;
      left: number;
      top: number;
      width: number;
      height: number;
    }> = [];

    for (let x = x0; x <= x1; x += 1) {
      const wx = wrapTileX(x, z);
      for (let y = y0; y <= y1; y += 1) {
        const nwLat = tile2lat(y, z);
        const nwLng = tile2lng(x, z);
        const seLat = tile2lat(y + 1, z);
        const seLng = tile2lng(x + 1, z);
        const [left, top] = latLngToPixel([nwLat, nwLng]);
        const [right, bottom] = latLngToPixel([seLat, seLng]);
        const w = right - left;
        const h = bottom - top;
        if (w <= 0 || h <= 0) continue;

        out.push({
          key: `${z}-${wx}-${y}-${x}`,
          url: urlTemplate
            .replace("{z}", String(z))
            .replace("{x}", String(wx))
            .replace("{y}", String(y)),
          left,
          top,
          width: w,
          height: h,
        });
      }
    }

    return out;
  }, [mapState, latLngToPixel, urlTemplate, maxNativeZoom, minZoom]);

  if (!tiles.length || !mapState?.width) return null;

  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: mapState.width,
        height: mapState.height,
        pointerEvents: "none",
        zIndex,
        overflow: "hidden",
        opacity,
        mixBlendMode: blendMode,
        background: "transparent",
      }}
      aria-hidden
    >
      {tiles.map((tile) => (
        <img
          key={tile.key}
          src={tile.url}
          alt=""
          draggable={false}
          loading="eager"
          style={{
            position: "absolute",
            left: tile.left,
            top: tile.top,
            width: tile.width,
            height: tile.height,
            userSelect: "none",
            background: "transparent",
            // Avoid white letterboxing while tiles decode
            imageRendering: "auto",
          }}
        />
      ))}
    </div>
  );
};

export default MapTileOverlay;
